import { request } from './client';

export type CouponType = 'percent' | 'fixed';

// Types for products and checkout
export interface ProductMeta {
  boardCredits?: number;
  featureKey?: string;
  maxManagers?: number;
  maxBoardSize?: number;
}

export interface PaymentPrice {
  id: string;
  productId: string;
  stripePriceId: string;
  amount: number; // Amount in cents
  currency: string;
  interval?: 'month' | 'year';
  intervalCount?: number;
  active: boolean;
  createdAt: string;
}

export type ProductType = 'credit_pack' | 'bundle' | 'feature' | 'subscription';

export interface BundleItem {
  id: string;
  bundleProductId: string;
  itemProductId: string;
  quantity: number;
  createdAt: string;
  itemProduct?: PaymentProduct;
}

export interface PaymentProduct {
  id: string;
  lookupKey: string;
  name: string;
  description: string;
  type: ProductType;
  stripeProductId: string;
  metadata: ProductMeta;
  active: boolean;
  displayOrder: number;
  prices: PaymentPrice[];
  bundleItems?: BundleItem[];
  createdAt: string;
  updatedAt: string;
}

export interface CreateCheckoutInput {
  priceId: string;
  couponCode?: string;
  boardId?: string;
  gaClientId?: string; // GA4 client_id for Measurement Protocol attribution
}

export interface CheckoutResult {
  clientSecret?: string;
  paymentIntentId?: string;
  transactionId: string;
  amount: number;
  currency: string;
  discountAmount?: number;
  productName: string;
  productType: string;
  creditsToReceive?: number;
  free?: boolean;           // True if 100% off coupon - no payment needed
  creditsGranted?: number;  // Credits already granted (for free checkouts)
}

export interface ConfirmPaymentInput {
  paymentIntentId: string;
}

export interface ConfirmPaymentResult {
  success: boolean;
  transactionId: string;
  creditsAdded?: number;
  newCreditBalance?: number;
  receiptUrl?: string;
}

export interface PaymentTransaction {
  id: string;
  userId: string;
  productId: string;
  priceId: string;
  stripePaymentIntentId: string;
  stripeChargeId?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'succeeded' | 'failed';
  couponId?: string;
  discountAmount: number;
  failureCode?: string;
  failureMessage?: string;
  cardBrand?: string;
  cardLast4?: string;
  paymentMethodType?: string;
  receiptUrl?: string;
  mode: 'test' | 'live';
  createdAt: string;
  updatedAt: string;
  product?: PaymentProduct;
}

export interface TransactionListResponse {
  transactions: PaymentTransaction[];
  total: number;
  limit: number;
  offset: number;
}

// Subscription types
export type SubscriptionStatus = 'active' | 'past_due' | 'canceled' | 'unpaid';

export interface Subscription {
  id: string;
  userId: string;
  productId: string;
  priceId: string;
  stripeSubscriptionId: string;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  canceledAt?: string;
  mode: 'test' | 'live';
  createdAt: string;
  updatedAt: string;
  product?: PaymentProduct;
  price?: PaymentPrice;
}

export interface CreateSubscriptionInput {
  priceId: string;
}

export interface CreateSubscriptionResult {
  subscriptionId: string;
  stripeSubscriptionId: string;
  clientSecret?: string; // For incomplete subscriptions requiring payment
  status: string;
}

export interface SubscriptionListResponse {
  subscriptions: Subscription[];
}

// Products API (public)
export const productsApi = {
  // List all active products
  async listProducts(): Promise<PaymentProduct[]> {
    return request<PaymentProduct[]>('/api/squareup/products');
  },

  // List credit pack products only
  async listCreditPacks(): Promise<PaymentProduct[]> {
    return request<PaymentProduct[]>('/api/squareup/products/credit-packs');
  },

  // Get a single product
  async getProduct(productId: string): Promise<PaymentProduct> {
    return request<PaymentProduct>(`/api/squareup/products/${productId}`);
  },
};

// Coupon validation types
export interface ValidateCouponInput {
  code: string;
  productId: string;
}

export interface ValidatedCoupon {
  id: string;
  code: string;
  type: CouponType;
  amountOff?: number;
  percentOff?: number;
  currency: string;
}

export interface CouponValidationResult {
  valid: boolean;
  coupon?: ValidatedCoupon;
  error?: string;
}

// Coupon validation API (authenticated)
export const couponsApi = {
  // Validate a coupon code for a specific product
  async validateCoupon(input: ValidateCouponInput): Promise<CouponValidationResult> {
    return request<CouponValidationResult>('/api/squareup/coupons/validate', {
      method: 'POST',
      body: input,
    });
  },
};

// Checkout API (authenticated)
export const checkoutApi = {
  // Create a checkout session
  async createCheckout(input: CreateCheckoutInput): Promise<CheckoutResult> {
    return request<CheckoutResult>('/api/squareup/checkout', {
      method: 'POST',
      body: input,
    });
  },

  // Confirm payment was successful
  async confirmPayment(input: ConfirmPaymentInput): Promise<ConfirmPaymentResult> {
    return request<ConfirmPaymentResult>('/api/squareup/checkout/confirm', {
      method: 'POST',
      body: input,
    });
  },
};

// Transactions API (authenticated)
export const transactionsApi = {
  // List user's transactions
  async listTransactions(limit = 20, offset = 0): Promise<TransactionListResponse> {
    return request<TransactionListResponse>(`/api/squareup/transactions?limit=${limit}&offset=${offset}`);
  },

  // Get a specific transaction
  async getTransaction(transactionId: string): Promise<PaymentTransaction> {
    return request<PaymentTransaction>(`/api/squareup/transactions/${transactionId}`);
  },
};

// Subscriptions API (authenticated)
export const subscriptionsApi = {
  // Create a new subscription
  async createSubscription(input: CreateSubscriptionInput): Promise<CreateSubscriptionResult> {
    return request<CreateSubscriptionResult>('/api/squareup/subscriptions', {
      method: 'POST',
      body: input,
    });
  },

  // List user's subscriptions
  async listSubscriptions(): Promise<SubscriptionListResponse> {
    return request<SubscriptionListResponse>('/api/squareup/subscriptions');
  },

  // Get a specific subscription
  async getSubscription(subscriptionId: string): Promise<Subscription> {
    return request<Subscription>(`/api/squareup/subscriptions/${subscriptionId}`);
  },

  // Cancel a subscription (at period end)
  async cancelSubscription(subscriptionId: string): Promise<{ message: string }> {
    return request<{ message: string }>(`/api/squareup/subscriptions/${subscriptionId}/cancel`, {
      method: 'POST',
    });
  },

  // Reactivate a canceled subscription
  async reactivateSubscription(subscriptionId: string): Promise<{ message: string }> {
    return request<{ message: string }>(`/api/squareup/subscriptions/${subscriptionId}/reactivate`, {
      method: 'POST',
    });
  },
};
